import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { getColorWithAlpha } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { getAnimation } from '../../../helpers/getAnimation';
import { StyledFreeLink, useTheme } from '../../common';
import { useSource } from '../../sources';
import { usePlayer } from '../PlayerProvider';
import { PlaylistItem, PlaylistItemComponent } from './PlaylistItem';
import { SpringScrollbars } from './SpringScrollbars';

const ITEM_MARGIN_RIGHT = '16px'; // Отступ трека от правого края плейлиста, чтобы трек не залезал на скроллбар

type HideDirection = 'toRight' | 'toTop';

interface PlaylistProps {
  maxHeight: string | number;
  hideDirection?: HideDirection;
  className?: string;
}

export const Playlist = forwardRef<HTMLDivElement, PlaylistProps>(
  ({ className, maxHeight, hideDirection }, ref) => {
    const { textColor } = useTheme();
    const { layout } = useSettings();
    const { items, currentItemDescriptionId } = useSource();
    const { showPlayer, showPlaylist } = usePlayer();

    const calculatedHeight = !['100%', 'auto'].includes(
      maxHeight.toString().toLowerCase()
    )
      ? parseInt(maxHeight.toString(), 10)
      : '100%';

    const scrollbarRef = useRef<SpringScrollbars | null>(null);
    const [hasVerticalScroll, setHasVerticalScroll] = useState(false);

    const scrollRefCallback = useCallback(
      (scrollbar: SpringScrollbars | null) => {
        if (scrollbar) {
          scrollbarRef.current = scrollbar;
          setHasVerticalScroll(scrollbar.getHasVerticalScroll());
        }
      },
      []
    );

    useEffect(() => {
      if (scrollbarRef.current) {
        setHasVerticalScroll(scrollbarRef.current.getHasVerticalScroll());
      }
    }, [currentItemDescriptionId]);

    const inBackground = ['floatingBackground', 'embedBackground'].includes(
      layout
    );

    if (!showPlayer || !items?.length) {
      return null;
    }

    return (
      <Component
        ref={ref}
        className={className}
        _showPlaylist={showPlaylist}
        _hideDirection={hideDirection}
        _isEmbedBackground={layout === 'embedBackground'}
      >
        <Container
          _hideDirection={hideDirection}
          _hasVerticalScroll={hasVerticalScroll}
          _showPlaylist={showPlaylist}
        >
          {/* @ts-expect-error Компонент не протипизирован */}
          <SpringScrollbars
            ref={scrollRefCallback}
            style={{
              width: '100%',
              height: '100%'
            }}
            renderThumbVertical={() => <Thumb _color={textColor} />}
            thumbMinSize={20}
            key={maxHeight}
            autoHeight
            autoHeightMin={0}
            autoHeightMax={calculatedHeight}
            renderTrackHorizontal={() => <div style={{ display: 'none' }} />}
            renderThumbHorizontal={() => <div style={{ display: 'none' }} />}
          >
            {!!items?.length &&
              items.map(
                (item, id) =>
                  item && <PlaylistItem data={item} key={id} id={id} />
              )}
          </SpringScrollbars>
        </Container>

        {inBackground && <StyledFreeLink />}
      </Component>
    );
  }
);

const Component = styled.div<{
  _showPlaylist: boolean;
  _isEmbedBackground: boolean;
  _hideDirection?: HideDirection;
}>`
  overflow: hidden;
  transition:
    ${getAnimation('opacity')}, ${getAnimation('transform')},
    ${getAnimation('color', 'ease', 0)};
  pointer-events: ${({ _showPlaylist }) => (_showPlaylist ? 'all' : 'none')};

  ${({ _showPlaylist, _isEmbedBackground }) => {
    if (_showPlaylist) {
      return;
    }

    if (_isEmbedBackground) {
      return css`
        background-color: transparent !important;
      `;
    }

    return css`
      opacity: 0;
    `;
  }}

  ${({ _hideDirection, _showPlaylist }) => {
    if (_hideDirection === 'toRight' && _showPlaylist) {
      return css`
        transform: translateX(0);
      `;
    }

    if (_hideDirection === 'toRight' && !_showPlaylist) {
      return css`
        transform: translateX(100%);
      `;
    }

    if (_hideDirection !== 'toRight') {
      return css`
        transform: translateX(0);
      `;
    }
  }}
`;

const Container = styled.div<{
  _hasVerticalScroll: boolean;
  _showPlaylist: boolean;
  _hideDirection?: HideDirection;
}>`
  transition: ${getAnimation()};
  opacity: ${({ _showPlaylist }) => (_showPlaylist ? 1 : 0)};

  ${({ _hideDirection, _showPlaylist }) =>
    _hideDirection === 'toTop' &&
    !_showPlaylist &&
    css`
      height: 0;
    `}

  ${({ _hideDirection }) =>
    _hideDirection === 'toRight' &&
    css`
      height: 100%;
    `}

  ${({ _hideDirection }) =>
    _hideDirection !== 'toRight' &&
    _hideDirection !== 'toTop' &&
    css`
      height: auto;
    `}

  & ${PlaylistItemComponent} {
    margin-right: ${({ _hasVerticalScroll }) =>
      _hasVerticalScroll ? ITEM_MARGIN_RIGHT : 0};
  }

  & > div > div:first-child {
    overflow-x: hidden !important;
  }
`;

const Thumb = styled.div<{
  _color: string;
}>`
  display: block !important;
  opacity: 1;
  position: relative;
  width: 8px;
  height: 100%;
  border-radius: 4px;
  cursor: pointer;
  transition: ${getAnimation('background-color')};
  background-color: ${({ _color }) => getColorWithAlpha(_color, 0.2)};

  &:hover {
    background-color: ${({ _color }) => getColorWithAlpha(_color, 0.9)};
  }
`;
